import type { FormEvent, ReactElement, ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import type { MeetingSummary } from '@contracts/meetings';
import type { UpcomingEvent } from '@contracts/calendar';
import {
  useBotAction,
  useFolders,
  useMe,
  useMeetings,
  useNames,
  useOrg,
  usePeople,
  useRecordOverride,
  useSeriesList,
  useSpaces,
  useUpcoming,
  useUpdateSeries,
  useUpdateSettings,
} from '../api/queries';
import type { MenuItem } from '../ui';
import {
  BackLink,
  Broken,
  Button,
  Card,
  Chip,
  Empty,
  ListSwitch,
  MenuButton,
  Rows,
  SearchField,
  Picker,
  SkeletonRows,
  useNotices,
} from '../ui';
import {
  between,
  clock,
  dayLabel,
  duration,
  groupByDay,
  isToday,
  since,
  isTomorrow,
  weekday,
} from '../lib/time';
import { guessName } from '../lib/note';
import { comingUp } from '../lib/coming-up';
import { useTab, useUrlParam } from '../lib/url-tab';
import { whyNothing } from '../meeting/why-nothing';

/** How many recorded meetings are drawn at once; "Show more" adds another. */
const PAGE = 30;

type Scope = 'all' | 'mine' | 'shared' | 'spaces';
type RangeKey = '7' | '30' | '90' | '0';
const RANGES: Array<{ value: RangeKey; label: string }> = [
  { value: '7', label: 'This week' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '0', label: 'Everything' },
];

/**
 * Boards 04 + 06 + 24 — meetings running forward as well as back, in one list
 * grouped by day: today and what is ahead first, then what was recorded.
 * Before a meeting the only question is whether it will be recorded, and every
 * row answers it the same way — when, what, who, one pill, one action.
 * "Couldn't get in" is the only state allowed to shout. Afterwards a row says
 * who was there and what the hour produced, and which space holds it; you see
 * everything you were in, whoever recorded it, and the scopes say which door.
 */
export function Meetings(): ReactElement {
  // The scope and every filter in the address (2026-09-25): Back from a
  // meeting, or a reload, finds the list as it was left.
  const [scope, setScope] = useTab<Scope>('scope', ['mine', 'shared', 'spaces', 'all'], 'mine');
  const [seriesParam, setSeriesParam] = useUrlParam('series');
  const series = seriesParam ?? '';
  const setSeries = (key: string) => setSeriesParam(key || null);
  const [personParam, setPersonParam] = useUrlParam('person');
  const personFilter = personParam ?? '';
  const setPersonFilter = (email: string) => setPersonParam(email || null);
  const [rangeKey, setRange] = useTab<RangeKey>('range', ['7', '30', '90', '0'], '30');
  const range = Number(rangeKey);
  /** How many recorded meetings are on screen; "Show more" adds another page. */
  const [shown, setShown] = useState(PAGE);
  const [needle, setNeedle] = useState('');
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const space = params.get('space') ?? undefined;
  const folder = params.get('folder') ?? undefined;
  const browsing = Boolean(space || folder);
  const me = useMe();
  const org = useOrg();
  const upcoming = useUpcoming(7);
  const past = useMeetings({
    ...(series ? { series } : {}),
    ...(personFilter ? { person: personFilter } : {}),
    ...(space ? { space } : {}),
    ...(folder ? { folder } : {}),
  });
  const seriesList = useSeriesList();
  const people = usePeople();
  const names = useNames();
  const spaces = useSpaces();
  const folders = useFolders();
  const theFolder = folder ? ((folders.data ?? []).find((f) => f.id === folder) ?? null) : null;
  const theSpace = (spaces.data ?? []).find((s) => s.id === (theFolder?.spaceId ?? space)) ?? null;
  const containerName = theFolder?.name ?? theSpace?.name ?? null;
  const myEmail = me.data?.email.toLowerCase() ?? '';
  const mySpaceIds = useMemo(
    () => new Set((spaces.data ?? []).filter((s) => s.myRole).map((s) => s.id)),
    [spaces.data],
  );

  /**
   * ⚠ Today, and tomorrow only when today is nearly over.
   *
   * This page is for finding a note from a meeting that has happened; a week
   * of calendar above it buried yesterday so thoroughly that Vladimir could
   * not find it at all (2026-09-21). His rule, and it is the right one: "show
   * only today's meetings in the future if needed, and if it's less than 3
   * left - show today's and tomorrow's, not more".
   */
  const all = useMemo(
    () =>
      (upcoming.data ?? []).filter(
        (e) =>
          (!series || e.seriesKey === series) &&
          // ⚠ Nothing a notetaker could ever join, and nothing anybody could
          // act on: a calendar block with no link, and a block with nobody
          // else in it. They filled Coming up with gym sessions and focus
          // time and pushed the real meetings off the card (Vladimir,
          // 2026-09-22: "remove from coming up meetings with no other
          // attendees and no meeting link so it dont cluter the view").
          // A capture that exists overrules it — if something DID record, the
          // row is about the recording, not about the invitation.
          (e.capture !== null || (e.plan !== 'no-meeting-url' && e.plan !== 'nobody-else-invited')),
      ),
    [upcoming.data, series],
  );
  // Today; tomorrow too when today is nearly over; the next day with any when
  // both are empty (`lib/coming-up.ts` has the rule and its reasons).
  const ahead = useMemo(() => (browsing ? [] : comingUp(all)), [all, browsing]);
  const todayAhead = ahead.filter((e) => isToday(e.startsAt));
  const willRecord = todayAhead.filter(
    (e) => e.plan === 'WILL_RECORD' || (e.capture !== null && e.capture !== 'FAILED'),
  ).length;
  // A meeting that already ran today is one row, not two: the calendar row carries it.
  const recordedElsewhere = new Set(ahead.map((e) => e.meetingId).filter(Boolean));
  const recordedAll = (past.data ?? []).filter((m) => !recordedElsewhere.has(m.id));
  const scopeOf = (m: MeetingSummary): Scope =>
    m.attendees.some((a) => a.toLowerCase() === myEmail)
      ? 'mine'
      : mySpaceIds.has(m.spaceId)
        ? 'spaces'
        : 'shared';
  const counts = {
    all: recordedAll.length,
    mine: recordedAll.filter((m) => scopeOf(m) === 'mine').length,
    shared: recordedAll.filter((m) => scopeOf(m) === 'shared').length,
    spaces: recordedAll.filter((m) => scopeOf(m) === 'spaces').length,
  };
  const since = Date.now() - range * 86_400_000;
  const recorded = recordedAll.filter(
    (m) =>
      (scope === 'all' || scopeOf(m) === scope) &&
      (range === 0 || Date.parse(m.startedAt) >= since),
  );
  useEffect(() => setShown(PAGE), [scope, range, series, personFilter, space, folder]);
  const page = recorded.slice(0, shown);
  const totals = useMemo(() => {
    const seconds = recorded.reduce((sum, m) => sum + between(m.startedAt, m.endedAt), 0);
    return { count: recorded.length, seconds };
  }, [recorded]);
  const calendarDown = me.data ? !me.data.calendar.connected : false;
  const spaceName = (id: string): string | null =>
    (spaces.data ?? []).find((s) => s.id === id)?.name ?? null;
  const search = (e: FormEvent) => {
    e.preventDefault();
    if (needle.trim().length >= 2) navigate(`/search?q=${encodeURIComponent(needle.trim())}`);
  };

  return (
    <div className="page stack-lg">
      {browsing ? (
        <div className="toolbar">
          <BackLink to="/channels">Collections</BackLink>
          <span className="crumb">
            {theSpace?.name ?? 'Your folders'}
            {theFolder ? ` · ${theFolder.name}` : ''}
          </span>
          <div className="actions">
            <Link to="/meetings" className="btn btn-sm btn-quiet">
              Everything
            </Link>
          </div>
        </div>
      ) : null}
      {browsing ? null : <ListSwitch on="meetings" />}
      <div className="page-head">
        <div className="stack" style={{ gap: 4 }}>
          <h1 className="t-title">{browsing ? (containerName ?? 'Meetings') : 'Meetings'}</h1>
          <p className="mono">
            {totals.count}{' '}
            {browsing ? 'meetings' : scope === 'all' && range === 0 ? 'recorded' : 'in this view'} ·{' '}
            {duration(totals.seconds)}
            {!browsing && recordedAll.length !== totals.count
              ? ` · ${recordedAll.length} recorded`
              : ''}
            {todayAhead.length
              ? ` · ${todayAhead.length} today, ${willRecord} of them will record`
              : ''}
          </p>
        </div>
        <form onSubmit={search} style={{ width: 320 }}>
          <SearchField
            placeholder="Title, person, or something said…"
            value={needle}
            onChange={(e) => setNeedle(e.target.value)}
            kbd=""
          />
        </form>
      </div>

      {calendarDown && !browsing ? (
        <div className="band band-red" role="alert">
          <span>
            <strong>Your calendar stopped answering.</strong> Nothing new will be recorded until it
            is reconnected — this is the one failure the app keeps saying until it is fixed.
          </span>
          <Link to="/settings#calendar" className="btn btn-sm btn-danger">
            Reconnect the calendar
          </Link>
        </div>
      ) : null}

      {browsing ? null : (
        <div className="between" style={{ alignItems: 'flex-start', gap: 12 }}>
          <div className="chips">
            <Chip on={scope === 'mine'} count={counts.mine} onClick={() => setScope('mine')}>
              Mine
            </Chip>
            <Chip on={scope === 'shared'} count={counts.shared} onClick={() => setScope('shared')}>
              Shared with me
            </Chip>
            <Chip on={scope === 'spaces'} count={counts.spaces} onClick={() => setScope('spaces')}>
              My spaces
            </Chip>
            <Chip on={scope === 'all'} count={counts.all} onClick={() => setScope('all')}>
              Everyone at {org.data?.name ?? 'the organisation'}
            </Chip>
          </div>
          <div className="cluster filters">
            <Picker
              aria-label="Series"
              value={series}
              onChange={setSeries}
              search
              width={180}
              options={[
                { value: '', label: 'Any series' },
                ...(seriesList.data ?? []).map((s) => ({ value: s.seriesKey, label: s.title })),
              ]}
            />
            <Picker
              aria-label="Person"
              value={personFilter}
              onChange={setPersonFilter}
              search
              width={170}
              options={[
                { value: '', label: 'Anyone' },
                ...(people.data ?? []).map((p) => ({
                  value: p.email,
                  label: p.name ?? p.email,
                  ...(p.name ? { sub: p.email } : {}),
                })),
              ]}
            />
            <Picker
              aria-label="How far back"
              value={rangeKey}
              onChange={setRange}
              width={160}
              options={RANGES}
            />
          </div>
        </div>
      )}
      {browsing ? null : (
        <p className="t-secondary" style={{ marginTop: -8 }}>
          {scope === 'mine'
            ? 'Every meeting you were a participant in — whoever recorded it, whichever space it files into.'
            : scope === 'shared'
              ? 'Meetings you were not in, shared with you by name or by link.'
              : scope === 'spaces'
                ? 'Meetings you were not in, in spaces you belong to.'
                : 'Everything you can open: attended, shared, or in your spaces.'}{' '}
          <Link to="/spaces">Spaces and folders →</Link>
        </p>
      )}

      {browsing ? (
        <div className="cluster">
          <Picker
            aria-label="Series"
            value={series}
            onChange={setSeries}
            search
            width={200}
            options={[
              { value: '', label: 'All series' },
              ...(seriesList.data ?? []).map((s) => ({ value: s.seriesKey, label: s.title })),
            ]}
          />
        </div>
      ) : null}

      {/*
        ⚠ Two cards, clearly apart, and **Coming up is small**.
        It was one card with a whole week of calendar on top, and Vladimir
        could not find yesterday's meetings at all: "I see only future meetings
        and I don't understand how to find notes from passed meetings and this
        is the main idea" — then "they are at the very bottom looks like far
        far in the future and these blocks are not split at all". Moving it
        below was worse again: "Coming up shall not be at the very bottom, what
        if I have 1000 meetings". So it stays on top and stops being big:
        today only, and tomorrow only when today is nearly over. Past notes are
        what this page is for; the next few hours are context.
      */}
      {browsing ? null : (
        <Card
          title="Coming up"
          flush
          aside={
            <span className="mono">
              {todayAhead.length
                ? `${todayAhead.length} today · ${willRecord} will record`
                : ahead.length
                  ? `nothing today · ${ahead.length} ${isTomorrow(ahead[0].startsAt) ? 'tomorrow' : `on ${weekday(ahead[0].startsAt)}`}`
                  : 'the next 7 days'}
            </span>
          }
        >
          {upcoming.isPending ? (
            <SkeletonRows rows={3} />
          ) : upcoming.isError ? (
            <Broken what="The week ahead" retry={() => void upcoming.refetch()} />
          ) : ahead.length === 0 ? (
            <div className="row dense" style={{ minHeight: 44 }}>
              <span className="t-secondary">
                Nothing on the calendar for the next 7 days{series ? ' in this series' : ''}.
                Meetings with a link appear here the moment they are on your calendar, already
                marked with whether they will be recorded.
              </span>
            </div>
          ) : (
            groupByDay(ahead, (e) => e.startsAt).map(({ day, items }) => (
              <DayGroup key={`ahead-${day}`} label={dayLabel(day)}>
                {items.map((e) => (
                  <AheadRow key={e.id} event={e} names={names} myEmail={myEmail} />
                ))}
              </DayGroup>
            ))
          )}
        </Card>
      )}
      <Card
        title="Recorded"
        flush
        aside={
          <span className="mono">{recorded.length ? `${recorded.length} · newest first` : ''}</span>
        }
      >
        {past.isPending ? (
          <SkeletonRows rows={5} />
        ) : past.isError ? (
          <Broken what="Your meetings" retry={() => void past.refetch()} />
        ) : recorded.length === 0 ? (
          <Empty
            lead={
              recordedAll.length
                ? 'Nothing in this view.'
                : series
                  ? 'Nothing recorded in this series yet.'
                  : browsing
                    ? 'Nothing in here yet.'
                    : 'Nothing recorded yet.'
            }
          >
            {recordedAll.length
              ? 'Widen the range, or another scope — the meetings are there.'
              : 'The first recorded meeting is written up within two minutes of the call ending, and lands here.'}
          </Empty>
        ) : (
          <>
            {groupByDay(page, (m) => m.startedAt).map(({ day, items }) => (
              <DayGroup key={`past-${day}`} label={dayLabel(day)}>
                {items.map((m) => (
                  <PastRow
                    key={m.id}
                    meeting={m}
                    names={names}
                    myEmail={myEmail}
                    spaceName={browsing ? null : spaceName(m.spaceId)}
                  />
                ))}
              </DayGroup>
            ))}
            {recorded.length > page.length ? (
              <div className="row dense" style={{ justifyContent: 'center', minHeight: 52 }}>
                <Button variant="quiet" onClick={() => setShown((n) => n + PAGE)}>
                  Show {Math.min(PAGE, recorded.length - page.length)} more ·{' '}
                  {recorded.length - page.length} older
                </Button>
              </div>
            ) : null}
          </>
        )}
      </Card>
    </div>
  );
}

function DayGroup({ label, children }: { label: string; children: ReactNode }): ReactElement {
  return (
    <div>
      <div className="row dense" style={{ background: 'var(--sunk)', minHeight: 32 }}>
        <span className="eyebrow">{label}</span>
      </div>
      <Rows>{children}</Rows>
    </div>
  );
}

const domainOf = (email: string): string => email.slice(email.indexOf('@') + 1).toLowerCase();

/** "Priya (guest), Nils, Ruth and 1 other" — the people, not a count; a guest is from outside. */
function whoElse(
  attendees: string[],
  myEmail: string,
  names: Map<string, string>,
  withYou = false,
): string {
  const others = attendees.filter((a) => a.toLowerCase() !== myEmail.toLowerCase());
  const you = withYou && attendees.some((a) => a.toLowerCase() === myEmail.toLowerCase());
  if (others.length === 0) return 'just you';
  const home = domainOf(myEmail);
  const shown = others.slice(0, 3).map((a) => {
    const first = (names.get(a.toLowerCase()) ?? guessName(a)).split(' ')[0];
    return home && domainOf(a) !== home ? `${first} (guest)` : first;
  });
  if (you) shown.push('you');
  const rest = others.length - Math.min(others.length, 3);
  return rest > 0
    ? `${shown.join(', ')} and ${rest} other${rest === 1 ? '' : 's'}`
    : shown.join(', ');
}

type State = {
  label: string;
  tone?: 'green' | 'amber' | 'red';
  dashed?: boolean;
  dot?: 'live' | 'green' | 'faint';
};

/** One pill per row, in the same place, and the same words the dispatcher uses. */
function stateOf(event: UpcomingEvent): State {
  switch (event.capture) {
    case 'SCHEDULED':
      return { label: 'Will record', tone: 'green', dot: 'green' };
    case 'JOINING':
      return { label: 'Joining now', tone: 'green', dot: 'green' };
    case 'WAITING_ROOM':
      return { label: 'At the door — admit it', tone: 'amber' };
    case 'RECORDING':
      return { label: 'In the call', tone: 'red', dot: 'live' };
    case 'PAUSED':
      return { label: 'Paused', tone: 'amber' };
    case 'DONE':
      return { label: 'Written up', tone: 'green' };
    case 'FAILED':
      return { label: whyNothing(event.captureReason).pill, tone: 'red' };
    case 'UNKNOWN':
      return { label: 'Unclear — checking' };
    case null:
      break;
  }
  switch (event.plan) {
    case 'WILL_RECORD':
      return { label: 'Will record', tone: 'green', dot: 'green' };
    case 'turned-off-for-this-meeting':
    case 'turned-off-for-this-series':
    case 'one-to-ones-off':
    case 'not-turned-on':
      return { label: 'Won’t record', dashed: true, dot: 'faint' };
    case 'no-meeting-url':
      return { label: 'No link' };
    case 'unsupported-platform':
      return { label: 'Not a Meet or Zoom link' };
    case 'nobody-else-invited':
      return { label: 'Just you' };
    case 'longer-than-a-meeting':
      return { label: 'Not a call' };
    case 'in-the-past':
      return { label: 'Not recorded', tone: 'amber' };
    case 'free-month-ended':
      return { label: 'Free month over', tone: 'amber' };
    default:
      return { label: 'Will record', tone: 'green', dot: 'green' };
  }
}

/** What the row says under the title — who, or why nothing can join. */
function subOf(event: UpcomingEvent, myEmail: string, names: Map<string, string>): string {
  const live = event.capture === 'RECORDING' || event.capture === 'PAUSED';
  if (live) return `started ${since(event.startsAt)} · ${whoElse(event.attendees, myEmail, names)}`;
  if (event.capture) return whoElse(event.attendees, myEmail, names);
  switch (event.plan) {
    case 'no-meeting-url':
      return 'the calendar event has no meeting link, so nothing can join';
    case 'unsupported-platform':
      return 'the link is not a Meet or Zoom one, so nothing can join';
    case 'longer-than-a-meeting':
      return 'longer than 6 hours — a block of time, not a call, whatever link it has';
    case 'one-to-ones-off':
      return 'you turned this off — one-to-ones are off in your settings';
    case 'not-turned-on':
      return `${whoElse(event.attendees, myEmail, names)} · you record only the meetings you turn on`;
    case 'turned-off-for-this-series':
      return `${whoElse(event.attendees, myEmail, names)} · you turned this series off`;
    case 'turned-off-for-this-meeting':
      return `${whoElse(event.attendees, myEmail, names)} · you skipped this one`;
    case 'free-month-ended':
      return `${whoElse(event.attendees, myEmail, names)} · the free month is over for everybody here who could record it`;
    default:
      return whoElse(event.attendees, myEmail, names);
  }
}

/** When, what, who, one pill, one action — the same shape in every state. */
function AheadRow({
  event,
  names,
  myEmail,
}: {
  event: UpcomingEvent;
  names: Map<string, string>;
  myEmail: string;
}): ReactElement {
  const bot = useBotAction();
  const updateSeries = useUpdateSeries();
  const settings = useUpdateSettings();
  const override = useRecordOverride();
  const { show } = useNotices();
  const state = stateOf(event);
  const live =
    event.capture !== null &&
    ['JOINING', 'WAITING_ROOM', 'RECORDING', 'PAUSED'].includes(event.capture);
  const oneToOne =
    event.attendees.filter((a) => a.toLowerCase() !== myEmail.toLowerCase()).length === 1;

  const act = (action: 'stop' | 'pause' | 'resume') => {
    if (!event.botRunId) return;
    bot.mutate(
      { runId: event.botRunId, action },
      {
        onSuccess: () =>
          show({
            tone: 'success',
            text:
              action === 'stop'
                ? `Stopped — the notetaker has left “${event.title}”. Everything it heard so far is kept.`
                : action === 'pause'
                  ? `Paused — the notetaker stays in “${event.title}” and records nothing until you resume.`
                  : `Recording “${event.title}” again.`,
          }),
        onError: (error) => show({ tone: 'failure', text: `That did not work: ${error.message}` }),
      },
    );
  };
  const thisOne = (record: boolean | null) =>
    override.mutate(
      { eventId: event.id, record },
      {
        onSuccess: () =>
          show({
            tone: 'success',
            text:
              record === false
                ? `Skipping “${event.title}” — just this one. The series is unchanged.`
                : record === true
                  ? `“${event.title}” will be recorded.`
                  : `“${event.title}” follows its series again.`,
            action: {
              label: 'Undo',
              onSelect: () => override.mutate({ eventId: event.id, record: event.recordOverride }),
            },
          }),
        onError: (error) => show({ tone: 'failure', text: error.message }),
      },
    );
  const series = (body: { botEnabled?: boolean; spaceId?: null }, text: string) => {
    if (!event.seriesKey) return;
    updateSeries.mutate(
      { key: event.seriesKey, body },
      {
        onSuccess: () => show({ tone: 'success', text }),
        onError: (error) => show({ tone: 'failure', text: error.message }),
      },
    );
  };
  const oneToOnes = (recordOneToOnes: boolean) =>
    settings.mutate(
      { recordOneToOnes },
      {
        onSuccess: () =>
          show({
            tone: 'success',
            text: recordOneToOnes
              ? 'One-to-ones are recorded again.'
              : 'One-to-ones are off from now on — a setting of your own, changeable in Settings.',
          }),
        onError: (error) => show({ tone: 'failure', text: error.message }),
      },
    );

  // Why this meeting will or will not be recorded, as four plain facts. The
  // menu below is built from them rather than from a chain of conditions, so
  // every state has the same menu with a different tick in it.
  const skipped = event.plan === 'turned-off-for-this-meeting';
  const seriesOff = event.plan === 'turned-off-for-this-series';
  const onesOff = event.plan === 'one-to-ones-off';
  const notOn = event.plan === 'not-turned-on';
  const off = skipped || seriesOff || onesOff || notOn;
  const booked = event.capture === 'SCHEDULED';

  const changeItems: MenuItem[] = [
    {
      label: skipped ? 'Record it after all' : off ? 'Record this one' : 'Record it',
      current: !off,
      sub: !off
        ? booked
          ? 'the notetaker is booked for this one'
          : 'the notetaker joins as it starts'
        : skipped
          ? 'follows the series again'
          : 'just this instance',
      // The undo for a meeting somebody skipped is "follow the rules again";
      // for one the rules turned off it is "record it anyway". Different
      // answers, and `null` vs `true` is the difference.
      ...(off ? { onSelect: () => thisOne(skipped ? null : true) } : {}),
    },
    {
      label: booked ? 'Skip this one — cancel it' : 'Skip just this one',
      current: skipped,
      sub: skipped
        ? 'you skipped it — the series is unchanged'
        : booked
          ? 'the notetaker is booked; this calls it off'
          : 'for a sensitive conversation',
      ...(skipped ? {} : { onSelect: () => thisOne(false) }),
    },
    ...(oneToOne && !notOn
      ? [
          {
            label: 'Never record one-to-ones',
            current: onesOff,
            sub: 'a setting of your own — every 1:1 from now on',
            ...(onesOff ? {} : { onSelect: () => oneToOnes(false) }),
          },
          ...(onesOff
            ? [
                {
                  label: 'Record one-to-ones again',
                  sub: 'a setting of your own',
                  onSelect: () => oneToOnes(true),
                },
              ]
            : []),
        ]
      : event.seriesKey
        ? [
            {
              label: 'Don’t record this series',
              current: seriesOff,
              sub: 'every future instance',
              ...(seriesOff
                ? {}
                : {
                    onSelect: () =>
                      series(
                        { botEnabled: false },
                        `The notetaker stays out of “${event.title}” from now on — every instance.`,
                      ),
                  }),
            },
            ...(seriesOff || notOn
              ? [
                  {
                    label: notOn ? 'Record every instance' : 'Record this series again',
                    sub: 'every future instance',
                    onSelect: () =>
                      series(
                        { botEnabled: true },
                        `“${event.title}” will be recorded from now on.`,
                      ),
                  },
                ]
              : []),
          ]
        : []),
    ...(event.seriesKey
      ? [
          {
            label: 'Record, but keep it in my own space',
            sub: 'yours, and every attendee gets it',
            onSelect: () =>
              series(
                { spaceId: null },
                `“${event.title}” will land in your own space from now on.`,
              ),
          },
        ]
      : []),
  ];

  return (
    <div className="row">
      <span className="mono" style={{ width: 52 }}>
        {clock(event.startsAt)}
      </span>
      <div className="row-main">
        <span className="row-title">
          {/* One page per meeting (PRD 44): the title is the way in, and a
              calendar block with no link is not a meeting and has none. */}
          {event.meetingId ? <Link to={`/m/${event.meetingId}`}>{event.title}</Link> : event.title}
        </span>
        <span className="row-sub">
          <span>{subOf(event, myEmail, names)}</span>
          {event.seriesKey ? (
            <>
              <span>·</span>
              <Link to={`/series/${encodeURIComponent(event.seriesKey)}`}>series</Link>
            </>
          ) : null}
        </span>
      </div>
      <span
        className={`pill ${state.tone ? `pill-${state.tone}` : ''} ${state.dashed ? 'pill-dashed' : ''}`}
      >
        {state.dot === 'live' ? <span className="dot dot-live" /> : null}
        {state.dot === 'green' ? <span className="dot dot-green" /> : null}
        {state.dot === 'faint' ? (
          <span className="dot" style={{ background: 'var(--hint)' }} />
        ) : null}
        {state.label}
      </span>
      <div className="row-actions">
        {live && event.capture === 'RECORDING' ? (
          <Button
            size="sm"
            onClick={() => act('pause')}
            working={bot.isPending ? 'Pausing…' : false}
          >
            Pause
          </Button>
        ) : null}
        {live && event.capture === 'PAUSED' ? (
          <Button
            size="sm"
            variant="primary"
            onClick={() => act('resume')}
            working={bot.isPending ? 'Resuming…' : false}
          >
            Resume
          </Button>
        ) : null}
        {live ? (
          <Button
            size="sm"
            variant="danger"
            onClick={() => act('stop')}
            working={bot.isPending ? 'Stopping…' : false}
          >
            Stop
          </Button>
        ) : null}
        {!live && event.capture === 'DONE' && event.meetingId ? (
          <Link to={`/m/${event.meetingId}`} className="btn btn-sm">
            Read the note
          </Link>
        ) : null}
        {!live && event.capture === 'FAILED' ? (
          <Button
            size="sm"
            variant="quiet"
            onClick={() =>
              show({
                tone: 'attention',
                text: whyNothing(event.captureReason).story(event.title),
              })
            }
          >
            What happened
          </Button>
        ) : null}
        {/* ⚠ ONE menu, on every meeting that has not started recording.
            There were four, each behind its own verdict, and a meeting with a
            booked notetaker matched none of them — `decideDispatch` answers
            "already-dispatched" first, so a row with a bot coming had no
            verdict to match. The menu therefore disappeared from exactly the
            meetings somebody wants to change (Vladimir, 2026-09-22: "Why for
            some meeting rows i see change option to not record meeting, but
            for most i dont, i want them all to have this option"). Four
            conditions that must together cover every case will not; one that
            always renders, with the current state ticked, will. While the bot
            is actually in the call the action is Stop, on the meeting. */}
        {/* A meeting you are invited to from a colleague's calendar (`mine`
            false) is here to be found and opened; whether it is recorded is
            decided on the calendar it came from — one meeting, one bot. */}
        {event.mine && !live && (!event.capture || event.capture === 'SCHEDULED') ? (
          <MenuButton size="sm" variant="quiet" label="Change" items={changeItems} />
        ) : !event.mine && !live ? (
          <span className="why">on a colleague’s calendar</span>
        ) : null}
      </div>
    </div>
  );
}

/** A row describes who was there and what the hour produced. The failed one is the only row allowed to shout. */
function PastRow({
  meeting,
  names,
  myEmail,
  spaceName,
}: {
  meeting: MeetingSummary;
  names: Map<string, string>;
  myEmail: string;
  spaceName: string | null;
}): ReactElement {
  const { show } = useNotices();
  const who = whoElse(meeting.attendees, myEmail, names, true);
  if (meeting.captureHealth === 'FAILED') {
    return (
      <div className="row" style={{ background: '#FBF1F0' }}>
        <span className="mono" style={{ width: 52 }}>
          {clock(meeting.startedAt)}
        </span>
        <div className="row-main">
          <span className="row-title red">
            <Link to={`/m/${meeting.id}`} style={{ color: 'inherit' }}>
              {meeting.title}
            </Link>
          </span>
          <span className="row-sub red">{whyNothing(meeting.captureReason).line}</span>
        </div>
        <span className="pill pill-red">{whyNothing(meeting.captureReason).pill}</span>
        <div className="row-actions">
          <Button
            size="sm"
            onClick={() =>
              show({
                tone: 'attention',
                text: whyNothing(meeting.captureReason).story(meeting.title),
              })
            }
          >
            What happened
          </Button>
        </div>
      </div>
    );
  }
  const produced =
    meeting.commitmentCount + meeting.decisionCount === 0
      ? meeting.enhanced
        ? 'produced nothing — a record of the conversation, which is fine'
        : 'being written up'
      : `produced ${count(meeting.commitmentCount, 'commitment')} and ${count(meeting.decisionCount, 'decision')}`;
  return (
    <Link to={`/m/${meeting.id}`} className="row">
      <span className="mono" style={{ width: 52 }}>
        {clock(meeting.startedAt)}
      </span>
      <div className="row-main">
        <span className="row-title">{meeting.title}</span>
        <span className="row-sub">
          {who} · {produced}
        </span>
      </div>
      {spaceName ? (
        <span className="pill">
          <span
            className="square"
            style={{ background: 'var(--green)', width: 7, height: 7, marginRight: 6 }}
          />
          {spaceName}
        </span>
      ) : null}
      <span className="mono">
        {duration(between(meeting.startedAt, meeting.endedAt))} ·{' '}
        {count(meeting.attendees.length, 'voice')}
      </span>
    </Link>
  );
}

const count = (n: number, noun: string): string => `${n} ${noun}${n === 1 ? '' : 's'}`;
